import type { AppliedScenarioMetricKey } from "@/types/applied-scenario.types";
import { magnitude } from "./format";

interface BiasValueProps {
  metric: AppliedScenarioMetricKey;
  /** Com SINAL. `AVG(realized − predicted)` — negativo = o gêmeo prometeu mais. */
  value: number | null;
}

// Spec 079 (FR-007) — o VIÉS, e o que o sinal dele significa.
//
// ⚠️ É o único número desta tela cujo sinal INVERTE o sentido da frase. Um "−3,2 pp" isolado exige
// que o leitor reconstrua de cabeça qual dos dois lados prometeu demais — e quem lê rápido erra.
// Deixar a interpretação por conta do leitor é onde a tela vira enigma.
//
// O erro médio (valor absoluto) responde "de quanto o gêmeo erra". O viés responde "para que lado"
// — e só o segundo diz se o modelo é otimista ou pessimista, que é a diferença entre calibrar para
// baixo e calibrar para cima.

export function BiasValue({ metric, value }: BiasValueProps) {
  // Nulo é AUSÊNCIA de par previsto+realizado, jamais viés zero. Um "0" aqui afirmaria que o gêmeo
  // acerta na média sobre uma métrica que ninguém mediu.
  if (value === null) {
    return <span className="text-text-tertiary">—</span>;
  }

  // Uma formatação só, lida duas vezes: o número e a frase falam da MESMA magnitude, e formatá-la
  // duas vezes é a porta pela qual "−3,2 pp" e "3,3 pp" acabam na mesma célula.
  const abs = magnitude(value, metric);
  // ⚠️ O SINAL SEGUE O NÚMERO EXIBIDO, não o bruto. O SQL arredonda a 2 casas e a tela mostra 1,
  // então um viés de 0,04 desenhava "+0 pp" acompanhado de "a fábrica entregou 0 pp a mais" — uma
  // frase que afirma uma diferença e a imprime como zero, na única célula que existe para não ser
  // ambígua. Abaixo da resolução exibida, "na média, o gêmeo acertou" é a leitura honesta.
  const roundsToZero = Math.abs(value) < 0.05;
  const signed = roundsToZero ? abs : `${value > 0 ? "+" : "−"}${abs}`;
  const sentence = roundsToZero
    ? "na média, o gêmeo acertou"
    : value < 0
      ? `o gêmeo prometeu ${abs} a mais do que a fábrica entregou`
      : `a fábrica entregou ${abs} a mais do que o gêmeo prometeu`;

  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className="font-mono tabular-nums text-text-primary">{signed}</span>
      {/* A frase acompanha o número em vez de virar tooltip: o sentido do sinal é a informação,
          e informação essencial atrás de hover não existe em toque nem em leitor de tela. */}
      <span className="text-right text-[11px] leading-tight text-text-secondary">{sentence}</span>
    </span>
  );
}
